import { useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'

import { IndikatorPovlacenja, usePovlacenje } from '../app/Osvjezavanje'
import { useLjuskaBezPomaka, usePovrsina } from '../app/tema'

/**
 * LJUSKA APLIKACIJE (Z15) — zajednička za gosta i vozača.
 *
 * Do Z15 su oba sučelja bila niz ekrana s vlastitim naslovom i poveznicom
 * "← Natrag": u pregledniku podnošljivo, u instaliranoj PWA slijepa ulica —
 * adresne trake nema, pa je jedini izlaz iz podekrana bio gumb koji je netko
 * morao ne zaboraviti staviti. Ljuska to okreće: **navigacija je namještaj, a ne
 * sadržaj ekrana**. Donji pojas stoji uvijek, zaglavlje uvijek zna gdje si, a
 * ekran ispod se bavi samo svojim poslom.
 *
 * Tri pravila koja ovdje stoje na jednom mjestu, da ih ne mora poštovati svaki
 * ekran za sebe (mockup `mockup-app/stil.css`, CLAUDE.md §17):
 *
 * 1. **donji pojas je svet** — fiksan je, nosi `safe-area-inset-bottom` i sadržaj
 *    ga NIKAD ne prekriva; platno zato završava iznad njega, uvijek;
 * 2. **dodirna meta ≥ 44px** — tab, ikona u zaglavlju i [+] su mete za palac u
 *    pokretu, ne sličice;
 * 3. **boje samo iz tokena** — nijedna heksadekadska vrijednost, pa tema tenanta
 *    mijenja izgled bez ijedne izmjene ovdje.
 *
 * Gost i vozač dijele isti namještaj jer imaju isti problem (jedna ruka, sunce,
 * pokret), a razlikuju se samo u tome što u njemu stoji: gost ima četiri taba,
 * vozač tri — ista rešetka, drugi broj stupaca.
 *
 * **[+] ima SAMO gost** (krug 10, nalaz 1). Kod njega otvara ponudu triju usluga
 * — radnju koja nije ni na jednom tabu i uvijek znači isto. Vozač je [+] imao
 * jedan krug (krug 9, "Vraćam auto") i to je bila greška mjesta, ne radnje: gumb
 * je stršao nad pojasom i preklapao susjedni tab, pa je meta za palac bila manja
 * od onoga što izgleda kao meta. Njegov jedan posao sada stoji na vrhu taba
 * "Zahtjevi", uz naloge kojima i pripada.
 */

/** Visina pojasa bez safe-area inseta; jedna vrijednost, ne po komponenti. */
const POJAS_PX = 66

/**
 * VISINA POJASA NA LIVING KOŽI (D42) — 72,5 px, izmjereno iz nacrta.
 *
 * Nacrtov pojas počinje na 745,5 px platna od 852, a donjih 34 px su crta za
 * povratak: ostaje 72,5. Zatečenih 66 px crta isti pojas 6,5 px niže, pa mu se i
 * krug "Pitajte Oxenia" spušta za isto toliko — strojni diff je to izmjerio na
 * OBA elementa, jer je to jedna mjera, a ne dvije.
 *
 * Vrijednost stoji UZ zatečenu i vrijedi samo living koži: pojas gosta na
 * zatečenoj naslovnici i pojas vozača ostaju 66 px, do zadnjeg piksela.
 */
export const POJAS_LIVING_PX = 72.5

/** Koliko platno završava iznad pojasa (pojas + zrak + [+] koji strši nad njim). */
export const DNO_PLATNA = `calc(${POJAS_PX}px + 34px + env(safe-area-inset-bottom, 0px))`

/** Donji rub sadržaja koji stoji NAD pojasom (list karte, poruka). */
export const IZNAD_POJASA = `calc(${POJAS_PX}px + 28px + env(safe-area-inset-bottom, 0px))`

/** Gornji rub zaglavlja: sadržaj klizi POD traku stanja (`viewport-fit=cover`). */
const VRH_ZAGLAVLJA = 'calc(env(safe-area-inset-top, 0px) + 16px)'

/**
 * Rubovi ekrana koji NEMA ljusku — prijava osoblja, ulazni stupac gosta, korijen.
 *
 * Ondje gdje ljuske nema, nema ni zaglavlja koje bi pokrilo traku stanja ni
 * pojasa koji bi pokrio traku geste, pa sigurnu zonu nosi sam stupac. Stoji uz
 * `DNO_PLATNA` i `IZNAD_POJASA`: geometrija ruba je jedan račun, a ne mjera koju
 * svaki ekran pogađa za sebe.
 */
export const VRH_EKRANA = 'calc(env(safe-area-inset-top, 0px) + 20px)'
export const DNO_EKRANA = 'calc(env(safe-area-inset-bottom, 0px) + 64px)'

/**
 * Okvir uređaja: uska kolona na sredini, puna visina, bez vanjskog skrolanja.
 *
 * Ovdje se objavljuju i dvije stvari o cijelom DOKUMENTU, jer su svojstvo ove
 * ljuske, a ne ijednog ekrana u njoj (Z15-polir §1 i §6):
 *
 * · **tema je noir** — podlogu nosi `<html>`, pa je ista i prije prvog
 *   renderiranja, između dvije rute i iza ruba zaslona (`app/tema.ts`);
 * · **dokument se ne pomiče** — ljuska je visoka točno jedan zaslon i skrola se
 *   samo platno u njoj, pa je svaki pomak dokumenta ispod nje otkrivanje
 *   podloge, a ne čitanje sadržaja.
 */
export function OkvirAplikacije({
  siroko = false,
  children,
}: {
  /**
   * ŽIVA KOŽA IDE OD RUBA DO RUBA (D42, 08.09.2026.).
   *
   * Zatečeni stupac je `max-w-sm` = 384 px, a svaki današnji telefon je širi
   * (393, 402, 430). Dok su ekrani plohe s razmakom od 20 px, tih se nekoliko
   * piksela ne vidi — podloga stupca i podloga stranice su ista boja. Living
   * naslovnica na vrh stavlja FOTOGRAFIJU, i tada se vidi odmah: slika staje
   * 4,5 px prije ruba zaslona i oko to čita kao okvir, a ne kao doček. Strojni
   * diff je to i izmjerio — svaka sekcija 9 px uža od nacrta i pomaknuta 4,5 px
   * udesno.
   *
   * Zato living resort dobiva stupac širine telefona (430 px = najširi iPhone),
   * a **sve ostalo ostaje na 384**: zatečena naslovnica i njezini ekrani ne
   * mijenjaju nijedan piksel, što je uvjet isporuke od L1 nadalje. Prekidač je
   * ISTI onaj koji bira naslovnicu i sredinu pojasa (`living/zastavica.ts`) —
   * treći račun o istoj postavci razišao bi se od prva dva.
   *
   * Na širokom ekranu i dalje stoji kolona telefona, a ne razvučena stranica:
   * ovo je gostova aplikacija, ne web.
   */
  siroko?: boolean
  children: ReactNode
}) {
  usePovrsina('noir')
  useLjuskaBezPomaka()

  return (
    <div
      className={`relative mx-auto flex h-dvh w-full flex-col overflow-hidden bg-noir ${
        siroko ? 'max-w-[430px]' : 'max-w-sm'
      }`}
    >
      {children}
    </div>
  )
}

/**
 * Zaglavlje: nadnaslov (gdje si) i naslov (što gledaš), uz jednu radnju desno.
 *
 * Naslov je `h1` i mijenja se s ekranom — čitač zaslona tako čuje promjenu
 * ekrana, a ne samo novi sadržaj ispod istog naslova.
 *
 * **Gornji rub je `safe-area-inset-top`, a ne konstanta** (Z15-polir §2). Uz
 * `viewport-fit=cover` sadržaj klizi pod traku stanja, pa je bez inseta nadnaslov
 * stajao ispod sata. Podloga je izrijekom noir preko cijele širine ljuske: ikone
 * trake stanja su bijele i crtaju se preko nas — sve što je ondje svijetlo znači
 * da sat i baterija nestaju.
 */
export function ZaglavljeAplikacije({
  nadnaslov,
  naslov,
  ispod,
  desno,
}: {
  nadnaslov?: ReactNode
  naslov: ReactNode
  /**
   * Redak ISPOD naslova — kod gosta soba i razdoblje boravka (krug 10, nalaz 7).
   *
   * Dotad su soba i razdoblje bili nadnaslov, pa naziv resorta nije imao gdje
   * stati: gost koji je aplikaciju instalirao vidi ikonu koja izgleda jednako u
   * svakom hotelu, a unutra nigdje nije pisalo čiji je ovo resort (znak marke
   * stoji samo na ulaznim ekranima, §17). Sad su to dvije tvrdnje na dva retka —
   * gore ČIJE je, dolje GDJE sam i dokad — umjesto jednog retka koji se pri
   * prvom dužem nazivu odreže na pola.
   */
  ispod?: ReactNode
  desno?: ReactNode
}) {
  return (
    <header
      className="relative z-20 flex shrink-0 items-center justify-between gap-3 bg-noir px-5 pb-3"
      style={{ paddingTop: VRH_ZAGLAVLJA }}
    >
      <div className="min-w-0">
        {/*
          `div`, ne `p`: nadnaslov je kod vozača cijeli blok (tko je prijavljen i
          u kojem svojstvu), a odlomak s odlomkom unutra nije ispravan dokument.
        */}
        {nadnaslov ? (
          <div className="mb-0.5 truncate text-[10px] tracking-[0.25em] text-brass uppercase">
            {nadnaslov}
          </div>
        ) : null}
        {/*
          CIJELO IME STANE (dotjerivanje-10 §3).

          Pozdrav je jedina stvar u zaglavlju koja nosi TUĐE ime, a ime se ne
          skraćuje: "Dobrodošli, Kristina Zetović Urb…" je gost koji na svom
          prvom ekranu vidi da ga kuća ne zna do kraja. Zato naslov prelama u
          dva retka umjesto da reže — dva su granica, jer treći bi zaglavlje
          podigao preko sadržaja (`line-clamp-2`), a `anywhere` je mreža za
          nepremostivo dugu jednu riječ.

          Nadnaslov i redak ispod ostaju kraćeni: ono su naše riječi (resort,
          soba, razdoblje), i njima je skraćivanje pošten kompromis.
        */}
        <h1 className="line-clamp-2 text-[22px] leading-tight text-ivory [overflow-wrap:anywhere]">
          {naslov}
        </h1>
        {/*
          SKRAĆENO, ALI NE ODREZANO (HITNO2 §3).

          Otkad uz sobu ide i tip iz katastra (D36), ovaj redak zna nositi
          "Soba 214 · Junior suite s pogledom na more i terasom · Balkon ·
          02.08.–07.08." — a širok je koliko i mobitel. Kraćenje ostaje (uz njega
          stoje datumi, a tri retka bi zaglavlje gurnula preko sadržaja), ali
          kraćenje bez punog teksta je REZANJE: gost vidi da mu kuća sobu ne zna
          do kraja. Zato ono što je odrezano stoji u `title`u, pa je dostupno
          dodirom i čitaču zaslona.

          Puni naziv se čita u tabu "Boravak" — ondje redak ima punu širinu i uz
          sebe nema datume, pa se ne kraći nego prelama (`components/noir.tsx`,
          `Redak`).
        */}
        {ispod ? (
          <p
            className="mt-0.5 truncate text-[12px] text-stone"
            title={typeof ispod === 'string' ? ispod : undefined}
          >
            {ispod}
          </p>
        ) : null}
      </div>
      {desno ? <div className="flex shrink-0 items-center gap-2">{desno}</div> : null}
    </header>
  )
}

/** Okrugla ikona u zaglavlju (QR skener, jezik) — meta za palac, ne sličica. */
export function IkonaGumb({
  oznaka,
  na,
  onClick,
  children,
}: {
  oznaka: string
  /** Poveznica kad je radnja navigacija; inače `onClick`. */
  na?: string
  onClick?: () => void
  children: ReactNode
}) {
  const razred =
    'grid size-11 shrink-0 place-items-center rounded-full border border-line bg-surface ' +
    'text-champagne transition-colors hover:border-brass hover:bg-noir'

  if (na) {
    return (
      <Link to={na} aria-label={oznaka} className={razred}>
        {children}
      </Link>
    )
  }

  return (
    <button type="button" aria-label={oznaka} onClick={onClick} className={razred}>
      {children}
    </button>
  )
}

/**
 * Platno: jedino što se skrola.
 *
 * `puno` je za ekran koji JEST kadar (karta): tada izlazi iz toka i puni okvir,
 * a pojas ostaje iznad njega — kadar s marginama izgleda kao slika karte, a ne
 * kao karta (mockup, `.scr.puna`).
 *
 * Visina je LANAC bez ijedne rupe, i to je uvjet da se karta vidi: `h-dvh` na
 * okviru → `flex-1 min-h-0` ovdje → `inset-0` na ekranu. Ekran koji puni kadar
 * mjeri visinu SVOG omotača, pa mu omotač ne smije biti kutija visoka nula
 * (vidi `ulaz-kadra` u `styles/index.css`).
 *
 * **DNO NOSI SADRŽAJ, NE SPREMNIK** (HITNO2 §4). Razmak kojim platno završava
 * iznad pojasa (`DNO_PLATNA`) stoji na kutiji SA sadržajem, a ne kao
 * `padding-bottom` samog `<main>`a. Razlika je nevidljiva dok se ne skrola do
 * kraja: donji padding spremnika koji se skrola dio je njegove pomične površine
 * samo ondje gdje ga preglednik takvim računa — WebKit ga zna ispustiti, i tada
 * zadnjih stotinjak piksela ekrana završi POD pojasom, dakle točno ono na što se
 * vlasnik požalio ("ne mogu doskrolati do gumba"). Padding na djetetu je obična
 * visina u toku i broji ga svaki motor jednako.
 *
 * **Ovdje živi i povlačenje za osvježavanje** (krug 10, nalaz 4). Stoji na
 * platnu, a ne u ekranu, iz istog razloga iz kojeg ovdje stoji i sve ostalo:
 * platno je JEDINO što se skrola, pa je jedino i može znati je li prst krenuo s
 * vrha. Ekran koji se time osvježava prijavljuje svoj dohvat
 * (`app/Osvjezavanje.tsx`), a gesta ne zna ništa o njemu. Kadar (karta) je
 * iznimka: ondje prst pomiče KARTU, pa bi gesta osvježavanja gostu vukla platno
 * ispod prsta umjesto da mu pomakne pogled.
 */
export function PlatnoAplikacije({
  puno = false,
  rub = true,
  children,
}: {
  puno?: boolean
  /**
   * Bočni razmak platna. `false` daje ekran OD RUBA DO RUBA — traži ga jedino
   * samostalan ekran (L1, living naslovnica), koji fotografiju crta preko cijele
   * širine i razmake nosi sam. Sve ostalo živi u koloni s 20 px, i to se ne mijenja.
   */
  rub?: boolean
  children: ReactNode
}) {
  // Element u STANJU, ne u `ref`u: ljuska ga pri prelasku na kartu zamijeni
  // drugim, a gesta mora prijeći s njim (vidi `usePovlacenje`).
  const [platno, postaviPlatno] = useState<HTMLElement | null>(null)
  const { pomak, osvjezava } = usePovlacenje(platno)

  if (puno) {
    return <main className="relative min-h-0 flex-1 overflow-hidden">{children}</main>
  }

  return (
    <main
      ref={postaviPlatno}
      data-platno="app"
      className={`relative min-h-0 flex-1 overflow-x-hidden overflow-y-auto ${rub ? 'px-5' : ''}`}
    >
      <IndikatorPovlacenja pomak={pomak} osvjezava={osvjezava} />

      {/*
        Sadržaj slijedi prst: guma se vidi na onome što se vuče, a ne samo na
        indikatoru. Prijelaz stoji samo pri POVRATKU (kad se prst pusti) —
        animirati svaki pomak prsta znači kašnjenje za prstom.

        Ista kutija nosi i dno platna (vidi gore): razmak iznad pojasa je visina
        SADRŽAJA, a ne padding spremnika koji se skrola.
      */}
      <div
        style={{
          paddingBottom: DNO_PLATNA,
          transform: pomak > 0 ? `translateY(${pomak}px)` : undefined,
          transition: pomak > 0 ? undefined : 'transform 180ms ease-out',
        }}
      >
        {children}
      </div>
    </main>
  )
}

export interface Tab {
  id: string
  na: string
  oznaka: string
  ikona: ReactNode
  /**
   * Broj koji gori na ikoni — nešto što korisnik još nije vidio (Z18).
   *
   * Stoji na TABU, a ne na ekranu, jer je to jedini način da se dozna za nešto
   * novo dok se gleda neki drugi ekran: poveznica u odlagalištu ("Boravak") vidi se
   * tek kad ju netko otvori. Nula i `undefined` su isto — bedž bez sadržaja je
   * oznaka koja svaki dan traži da ju se pročita.
   */
  bedz?: number

  /**
   * MJESTO KOJE ČEKA SIDRO (dotjerivanje-3 §7).
   *
   * Pojas gosta slaže tenant, pa se dva izborna mjesta znaju tek kad stigne
   * `/me/access`. Do tada se NE POGAĐA: crta se prazno mjesto iste širine, koje
   * ništa ne otvara i koje čitač zaslona preskače. Sidro koje bi se pojavilo pa
   * zamijenilo drugim je promašen tap, a promašen tap u pojasu je jedina meta na
   * koju palac ide napamet.
   */
  kostur?: boolean
}

/**
 * Donji pojas s tabovima. Sredina se prazni kad postoji [+] (gost): rešetka
 * dobiva prazan stupac, jer bi FAB inače stajao NA tabu i krao mu dodir.
 */
export function Pojas({
  oznaka,
  tabovi,
  aktivan,
  sredina = false,
  visina = POJAS_PX,
  krupno = false,
}: {
  /** Ime navigacije za čitač zaslona; i18n ostaje na pozivatelju. */
  oznaka: string
  tabovi: Tab[]
  aktivan: string | null
  /** Ostavi prazan stupac u sredini za istaknuti [+]. */
  sredina?: boolean
  /** Visina bez safe-area inseta; living koža nosi nacrtovih 72,5 (D42). */
  visina?: number
  /** Nacrtova mjera taba (D42): krupniji znak, veći natpis, sadržaj uz vrh. */
  krupno?: boolean
}) {
  const lijevo = sredina ? tabovi.slice(0, Math.ceil(tabovi.length / 2)) : tabovi
  const desno = sredina ? tabovi.slice(Math.ceil(tabovi.length / 2)) : []

  const stupci = sredina
    ? `repeat(${lijevo.length},1fr) 78px repeat(${desno.length},1fr)`
    : `repeat(${tabovi.length},1fr)`

  /*
    MJERA TABA NA LIVING KOŽI (D42) — sve četiri vrijednosti su iz nacrta: znak
    25,4 px (umjesto 21), natpis 11,5 (umjesto 10,5), razmak među njima 6,45 i
    sadržaj koji počinje 11,6 px od ruba pojasa umjesto da stoji u sredini.

    Piše se kao natkrivanje nad stavkom, a ne kao druga komponenta: tab je isti
    tab (isti znak, isti natpis, isti bedž), samo krupnije crtan — dvije bi
    komponente značile dva mjesta na kojima se sutra popravlja isti bedž.
  */
  const mjera = krupno
    ? '[&_a]:justify-start [&_a]:gap-[6.45px] [&_a]:pt-[11.6px] [&_a]:text-[11.5px] [&_a]:leading-[12px] [&_svg]:size-[25.4px]'
    : ''

  return (
    <nav
      aria-label={oznaka}
      className={`absolute inset-x-0 bottom-0 z-30 grid items-center border-t border-line bg-noir/95 backdrop-blur ${mjera}`}
      style={{
        gridTemplateColumns: stupci,
        height: `calc(${visina}px + env(safe-area-inset-bottom, 0px))`,
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      {lijevo.map((tab) => (
        <StavkaPojasa key={tab.id} tab={tab} aktivan={tab.id === aktivan} />
      ))}
      {sredina ? <span aria-hidden="true" /> : null}
      {desno.map((tab) => (
        <StavkaPojasa key={tab.id} tab={tab} aktivan={tab.id === aktivan} />
      ))}
    </nav>
  )
}

function StavkaPojasa({ tab, aktivan }: { tab: Tab; aktivan: boolean }) {
  // Mjesto bez sidra: nije poveznica (nema kamo), nema teksta (nema što reći) i
  // ne postoji za čitač zaslona — samo drži širinu dok odgovor ne stigne.
  if (tab.kostur) {
    return (
      <span
        aria-hidden="true"
        data-kostur="tab"
        className="flex h-full flex-col items-center justify-center gap-1.5"
      >
        <span className="size-5 rounded-full kostur" />
        <span className="h-2 w-8 rounded-full kostur" />
      </span>
    )
  }

  return (
    <Link
      to={tab.na}
      aria-current={aktivan ? 'page' : undefined}
      className={`relative flex h-full min-h-11 flex-col items-center justify-center gap-1 text-[10.5px] transition-colors ${
        aktivan ? 'text-champagne' : 'text-stone'
      }`}
    >
      {/* Zlatna nit iznad aktivnog taba — ista oznaka kao u maketi. */}
      {aktivan ? (
        <span aria-hidden="true" className="absolute top-0 h-0.5 w-6 rounded-b bg-brass" />
      ) : null}
      <span className="relative">
        {tab.ikona}
        {tab.bedz ? (
          <span
            aria-hidden="true"
            className="absolute -top-1 -right-2 grid min-w-4 place-items-center rounded-full bg-brass px-1 text-[9px] leading-4 font-semibold text-noir"
          >
            {tab.bedz > 9 ? '9+' : tab.bedz}
          </span>
        ) : null}
      </span>
      {/*
        Brojka je `aria-hidden` jer ju čitač zaslona mora čuti kao dio IMENA
        taba, a ne kao broj koji lebdi pored ikone — inače se pročita dvaput,
        drugi put bez konteksta.
      */}
      {tab.bedz ? `${tab.oznaka} (${tab.bedz})` : tab.oznaka}
    </Link>
  )
}

/** Istaknuti okrugli [+] u sredini pojasa — jedina radnja koja je uvijek ista. */
export function Fab({ oznaka, onClick }: { oznaka: string; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label={oznaka}
      onClick={onClick}
      className="absolute left-1/2 z-[31] grid size-15 -translate-x-1/2 place-items-center rounded-full border border-champagne bg-brass text-noir shadow-lg transition-transform active:scale-95"
      style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}
    >
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
        <path d="M12 5v14M5 12h14" />
      </svg>
    </button>
  )
}

/**
 * Donji list (bottom sheet) — izbor koji ne zaslužuje vlastiti ekran.
 *
 * Zastor hvata dodir izvan lista: na mobitelu se izlazi prstom pored, a ne
 * traženjem malog "x". `role="dialog"` uz `aria-modal` jer dok stoji, ispod se
 * ništa ne dira.
 */
export function List({
  otvoren,
  naslov,
  opis,
  naZatvaranje,
  izPlatna = false,
  children,
}: {
  otvoren: boolean
  naslov: string
  opis?: string
  naZatvaranje: () => void
  /**
   * LIST KOJI OTVARA EKRAN, A NE LJUSKA (D28 §2).
   *
   * Zadano je `absolute`, jer list [+] otvara sama ljuska — a ona je jedini
   * `relative` predak na dnu zaslona. Kartica na PLATNU taj sidro nema: platno
   * je `relative` i skrola se (`components/ljuska.tsx`, `Platno`), pa bi se list
   * usidrio na njega i otišao s ekranom kad gost povuče prst. Zato ovaj oblik
   * ide kroz portal na `document.body` i `fixed` — jedan zaslon, jedno dno.
   *
   * **Ista komponenta, ne druga**: dva lista koja rade istu stvar ne smiju
   * izgledati kao dvije različite stvari (isti zakon kao `Stavka` ispod).
   */
  izPlatna?: boolean
  children: ReactNode
}) {
  if (!otvoren) return null

  const sidro = izPlatna ? 'fixed' : 'absolute'

  const sadrzaj = (
    <>
      <button
        type="button"
        aria-hidden="true"
        tabIndex={-1}
        onClick={naZatvaranje}
        className={`ulaz-zastora ${sidro} inset-0 z-40 bg-black/60 backdrop-blur-[2px]`}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={naslov}
        className={
          `ulaz-lista ${sidro} inset-x-0 bottom-0 z-[41] rounded-t-3xl border-t border-brass ` +
          'bg-surface px-5 pt-2.5' +

          // Kroz portal list izlazi iz uske kolone ljuske (`max-w-sm`), pa ju
          // mora ponoviti sam — inače bi na tabletu bio širok koliko i prozor,
          // dok aplikacija iznad njega stoji u sredini.
          (izPlatna ? ' mx-auto max-w-sm' : '')
        }
        style={{ paddingBottom: 'calc(24px + env(safe-area-inset-bottom, 0px))' }}
      >
        <div aria-hidden="true" className="mx-auto mb-4 h-1 w-10 rounded-full bg-line" />
        <h2 className="text-[19px] text-ivory">{naslov}</h2>
        {opis ? <p className="mt-0.5 mb-3 text-[13px] text-stone">{opis}</p> : <div className="mb-3" />}
        {children}
      </div>
    </>
  )

  return izPlatna ? createPortal(sadrzaj, document.body) : sadrzaj
}

/**
 * Stavka lista/popisa: naziv lijevo, dopuna i strelica desno.
 *
 * Isti oblik nosi izbor u [+] listu i popis u "Boravak" — dvije liste koje rade
 * istu stvar ne smiju izgledati kao dvije različite stvari.
 */
export function Stavka({
  na,
  onClick,
  lijevo,
  desno,
  opis,
  nedostupno,
}: {
  na?: string
  onClick?: () => void
  lijevo: ReactNode
  desno?: ReactNode
  opis?: ReactNode
  /**
   * ZAŠTO SE OVO SADA NE MOŽE (dotjerivanje-8 §3). Kad stoji, redak se ne tapa —
   * i to je namjerno: usluga koja u ovoj fazi boravka ne postoji ne smije voditi
   * u obrazac koji završava na 403.
   *
   * **Stavka se NE SKRIVA nego prigušuje, s razlogom u istom retku.** Skrivena
   * usluga je usluga koje "nema" — gost koji je jučer naručivao buggy ne bi znao
   * je li se pokvarila aplikacija ili resort. Prigušena s rečenicom ("od 14 h,
   * kad preuzmete sobu") odgovara na pitanje prije nego ga postavi.
   */
  nedostupno?: ReactNode
}) {
  const sadrzaj = (
    <>
      <span className="min-w-0">
        <span className={`block truncate ${nedostupno ? 'text-stone' : 'text-ivory'}`}>
          {lijevo}
        </span>
        {nedostupno || opis ? (
          <span className="mt-0.5 block text-[12.5px] text-stone">{nedostupno ?? opis}</span>
        ) : null}
      </span>
      <span className="shrink-0 text-[13px] text-stone">
        {nedostupno ? null : (
          <>
            {desno} <span aria-hidden="true">›</span>
          </>
        )}
      </span>
    </>
  )

  const razred =
    'flex min-h-11 w-full items-center justify-between gap-3 border-b border-line py-3 ' +
    'text-left text-[14.5px] last:border-b-0'

  // Nije ni poveznica ni gumb: meta koja ne vodi nikamo je meta koja krade dodir
  // (§17, "istaknut gumb koji krade dodir susjedu je zamka").
  if (nedostupno) {
    return (
      <div aria-disabled="true" data-nedostupno="da" className={`${razred} opacity-60`}>
        {sadrzaj}
      </div>
    )
  }

  if (na) {
    return (
      <Link to={na} className={razred}>
        {sadrzaj}
      </Link>
    )
  }

  return (
    <button type="button" onClick={onClick} className={razred}>
      {sadrzaj}
    </button>
  )
}

/** Naslov sekcije unutar ekrana (mockup `.sekcija`). */
export function Sekcija({ children }: { children: ReactNode }) {
  return (
    <h2 className="mt-5 mb-2.5 text-[10px] tracking-[0.22em] text-brass uppercase first:mt-2">
      {children}
    </h2>
  )
}
